import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { useGetMessagesQuery } from '../store/services/whatsappApi';

// Import the component we'll use in the split view
import Messages from './Messages';

interface Contact {
  id: number;
  name: string;
  phone_number: string;
}

interface Message {
  id: number;
  contact_id: number;
  content: string;
  timestamp: string;
  direction: string;
  status: string;
  message_type: string;
  contact?: Contact;
}

interface Conversation {
  contactId: number;
  contactName: string;
  phoneNumber: string;
  lastMessage: string;
  lastMessageTime: string;
  latestInboundTime: string | null;
  latestOutboundTime: string | null;
  unreadCount: number;
  lastMessageStatus: string;
  lastMessageDirection: string;
}

const ChatLayout: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    contactId ? parseInt(contactId) : null
  );
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const [isLoadingAllMessages, setIsLoadingAllMessages] = useState(true);

  // State for pagination
  const [currentSkip, setCurrentSkip] = useState(0);
  const batchSize = 100;

  // Fetch messages in batches
  const { data: messagesBatch, isLoading: isBatchLoading, isFetching: isBatchFetching } = useGetMessagesQuery(
    { limit: batchSize, skip: currentSkip },
    { skip: !isLoadingAllMessages }
  );

  // Accumulate messages from batches
  useEffect(() => {
    if (messagesBatch && !isBatchFetching) {
      const newMessages = messagesBatch.messages || [];
      setAllMessages(prev => [...prev, ...newMessages]);

      // If we got a full batch, there might be more; fetch next batch
      if (newMessages.length === batchSize) {
        setCurrentSkip(prev => prev + batchSize);
      } else {
        // No more messages to fetch
        setIsLoadingAllMessages(false);
      }
    }
  }, [messagesBatch, isBatchFetching, batchSize]);

  // Reset when starting fresh
  useEffect(() => {
    if (isLoadingAllMessages && currentSkip === 0) {
      setAllMessages([]);
    }
  }, [isLoadingAllMessages, currentSkip]);
  
  // Process messages into conversations
  useEffect(() => {
    if (allMessages && allMessages.length > 0) {
      const conversationsMap = new Map<number, Conversation>();

      // Group messages by contact
      allMessages.forEach((message: Message) => {
        if (!message.contact_id || !message.contact) return;
        
        const contactId = message.contact_id;
        const existingConversation = conversationsMap.get(contactId);
        
        // Parse message timestamp
        const messageTime = new Date(message.timestamp);
        
        if (existingConversation) {
          // Update existing conversation if this message is newer
          const existingTime = new Date(existingConversation.lastMessageTime);
          if (messageTime > existingTime) {
            existingConversation.lastMessage = message.content || (message.message_type === 'media' ? '[Media]' : '[Message]');
            existingConversation.lastMessageTime = message.timestamp;
            existingConversation.lastMessageStatus = message.status;
            existingConversation.lastMessageDirection = message.direction;
          }
          // Update latest inbound/outbound times
          if (message.direction === 'inbound') {
            const currentInboundTime = existingConversation.latestInboundTime ? new Date(existingConversation.latestInboundTime) : new Date(0);
            if (messageTime > currentInboundTime) {
              existingConversation.latestInboundTime = message.timestamp;
            }
          } else {
            const currentOutboundTime = existingConversation.latestOutboundTime ? new Date(existingConversation.latestOutboundTime) : new Date(0);
            if (messageTime > currentOutboundTime) {
              existingConversation.latestOutboundTime = message.timestamp;
            }
          }
          conversationsMap.set(contactId, existingConversation);
        } else {
          // Create new conversation
          const conversation: Conversation = {
            contactId,
            contactName: message.contact.name,
            phoneNumber: message.contact.phone_number,
            lastMessage: message.content || (message.message_type === 'media' ? '[Media]' : '[Message]'),
            lastMessageTime: message.timestamp,
            latestInboundTime: message.direction === 'inbound' ? message.timestamp : null,
            latestOutboundTime: message.direction === 'outbound' ? message.timestamp : null,
            unreadCount: 0, // We'll implement this later
            lastMessageStatus: message.status,
            lastMessageDirection: message.direction
          };
          conversationsMap.set(contactId, conversation);
        }
      });
      
      // Convert map to array and sort by most recent inbound message, then outbound
      const sortedConversations = Array.from(conversationsMap.values())
        .sort((a, b) => {
          const aInboundTime = a.latestInboundTime ? new Date(a.latestInboundTime).getTime() : 0;
          const bInboundTime = b.latestInboundTime ? new Date(b.latestInboundTime).getTime() : 0;

          if (aInboundTime !== bInboundTime) {
            return bInboundTime - aInboundTime; // Most recent inbound first
          }

          // If inbound times are equal (or both null), sort by outbound time
          const aOutboundTime = a.latestOutboundTime ? new Date(a.latestOutboundTime).getTime() : 0;
          const bOutboundTime = b.latestOutboundTime ? new Date(b.latestOutboundTime).getTime() : 0;
          return bOutboundTime - aOutboundTime;
        });
      
      setConversations(sortedConversations);

      // If no contact is selected and we have conversations, select the first one
      if (!selectedContactId && sortedConversations.length > 0) {
        setSelectedContactId(sortedConversations[0].contactId);
      }
    }
  }, [allMessages, selectedContactId]);
  
  // Handle contact selection
  const handleContactSelect = (contactId: number) => {
    setSelectedContactId(contactId);
  };
  
  // Filter conversations based on search term
  const filteredConversations = conversations.filter(
    conversation => 
      conversation.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conversation.phoneNumber.includes(searchTerm)
  );
  
  return (
    <MainLayout>
      <div className="flex h-full bg-gray-100 rounded-lg shadow overflow-hidden" style={{ height: 'calc(100vh - 100px)' }}>
        {/* Left side - Conversations List */}
        <div className="w-1/3 border-r border-gray-300 flex flex-col bg-white h-full overflow-hidden">
          {/* Search Header */}
          <div className="bg-gray-100 p-3 border-b">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search contacts or messages"
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {isLoadingAllMessages || isBatchLoading ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                {searchTerm ? 'No conversations match your search.' : 'No conversations yet.'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredConversations.map((conversation) => (
                  <li 
                    key={conversation.contactId}
                    onClick={() => handleContactSelect(conversation.contactId)}
                    className={`hover:bg-gray-50 cursor-pointer ${
                      selectedContactId === conversation.contactId ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="px-4 py-3 flex items-center">
                      {/* Contact Avatar */}
                      <div className="w-12 h-12 rounded-full bg-whatsapp-light-green text-whatsapp-green flex items-center justify-center font-bold text-xl mr-3">
                        {conversation.contactName.charAt(0).toUpperCase()}
                      </div>
                      
                      {/* Conversation Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {conversation.contactName}
                          </h3>
                          <span className="text-xs text-gray-500">
                            {getTimeAgo(new Date(conversation.lastMessageTime))}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <p className="text-sm text-gray-500 truncate flex-1">
                            {conversation.lastMessageDirection === 'outbound' && (
                              <span className="mr-1">
                                {conversation.lastMessageStatus === 'delivered' && '✓✓ '}
                                {conversation.lastMessageStatus === 'read' && <span className="text-blue-500">✓✓ </span>}
                                {conversation.lastMessageStatus === 'sent' && '✓ '}
                                {conversation.lastMessageStatus === 'failed' && '⚠️ '}
                              </span>
                            )}
                            {conversation.lastMessage}
                          </p>
                          {conversation.unreadCount > 0 && (
                            <span className="ml-2 bg-whatsapp-green text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                              {conversation.unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        {/* Right side - Messages */}
        <div className="w-2/3 flex flex-col h-full overflow-hidden">
          {selectedContactId ? (
            <Messages contactId={selectedContactId} />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center p-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No conversation selected</h3>
                <p className="mt-1 text-sm text-gray-500">Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

// Helper function to format time ago
function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.round(diffMs / 1000);
  const diffMins = Math.round(diffSecs / 60);
  const diffHours = Math.round(diffMins / 60);
  const diffDays = Math.round(diffHours / 24);

  if (diffSecs < 60) {
    return 'just now';
  } else if (diffMins < 60) {
    return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  } else {
    // Format date for older messages
    return date.toLocaleDateString();
  }
}

export default ChatLayout;
